import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Button,
  Space,
  Spin,
  Progress,
  Typography,
  Tooltip,
  Empty,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSelector as useSel } from 'react-redux';
import { fetchBillPaymentsRequest } from '../../redux/actions/paymentActions';
import { hasPermission } from '../../utils/permissions';
import RecordPaymentModal from './RecordPaymentModal';

const { Text } = Typography;

const fmtMoney = (val) =>
  val != null
    ? `₹ ${Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
    : '—';

const fmtDate = (val) =>
  val ? dayjs(val).format('DD MMM YYYY') : '—';

export const getPaymentStatusTag = (status) => {
  const map = {
    unpaid:         { color: 'red',     icon: <CloseCircleOutlined />,  text: 'Unpaid'         },
    partially_paid: { color: 'orange',  icon: <ClockCircleOutlined />,  text: 'Partially Paid' },
    fully_paid:     { color: 'success', icon: <CheckCircleOutlined />,  text: 'Fully Paid'     },
  };
  const cfg = map[status] || { color: 'default', icon: null, text: status || '—' };
  return (
    <Tag color={cfg.color} icon={cfg.icon} style={{ fontWeight: 500 }}>
      {cfg.text}
    </Tag>
  );
};

const getPaymentModeTag = (mode) => {
  const colors = { neft: 'blue', rtgs: 'geekblue', cheque: 'purple', cash: 'green', upi: 'cyan', dd: 'magenta' };
  return <Tag color={colors[mode] || 'default'}>{(mode || '').toUpperCase()}</Tag>;
};

const getStatusTag = (status) => {
  if (status === 'completed') return <Tag color="success">Completed</Tag>;
  if (status === 'pending')   return <Tag color="warning">Pending</Tag>;
  if (status === 'failed')    return <Tag color="error">Failed</Tag>;
  return <Tag>{status}</Tag>;
};

/**
 * PaymentPanel
 *
 * Displays payment summary cards + payment history table for a bill.
 * Used inside BillReceiptDetail.
 *
 * Props:
 *  bill — KpBillReceipt (must have id, bill_number, bill_amount, bill_status)
 */
const PaymentPanel = ({ bill }) => {
  const dispatch = useDispatch();
  const permissions = useSel((s) => s.auth.permissions || []);
  const { billPayments, loading } = useSelector((s) => s.payments);

  const canCreate = hasPermission(permissions, 'bill_receipt/create');
  const [modalOpen, setModalOpen] = useState(false);

  const billId = bill?.id;
  const cached = billPayments[billId];
  const summary = cached?.summary;
  const payments = cached?.payments || [];

  useEffect(() => {
    if (billId) {
      dispatch(fetchBillPaymentsRequest(billId));
    }
  }, [billId, dispatch]);

  const handleRefresh = () => {
    if (billId) dispatch(fetchBillPaymentsRequest(billId));
  };

  if (!bill) return null;

  const billAmount   = summary?.bill_amount   ?? bill.bill_amount ?? 0;
  const totalPaid    = summary?.total_paid    ?? 0;
  const outstanding  = summary?.outstanding   ?? billAmount;
  const paidPercent  = billAmount > 0 ? Math.min(100, Math.round((totalPaid / billAmount) * 100)) : 0;
  const payStatus    = summary?.payment_status ?? 'unpaid';

  const columns = [
    {
      title: '#',
      key: 'idx',
      width: 36,
      render: (_, __, i) => <Text type="secondary" style={{ fontSize: 11 }}>{i + 1}</Text>,
    },
    {
      title: 'Date',
      dataIndex: 'payment_date',
      key: 'payment_date',
      width: 110,
      render: fmtDate,
    },
    {
      title: 'Mode',
      dataIndex: 'payment_mode',
      key: 'payment_mode',
      width: 90,
      render: getPaymentModeTag,
    },
    {
      title: 'Reference',
      dataIndex: 'reference_number',
      key: 'reference_number',
      width: 150,
      ellipsis: true,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'right',
      render: (v) => <Text strong style={{ color: '#1890ff' }}>{fmtMoney(v)}</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: getStatusTag,
    },
    {
      title: 'Paid By',
      dataIndex: 'paid_by_name',
      key: 'paid_by_name',
      width: 120,
      ellipsis: true,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      ellipsis: true,
      render: (v) => v ? <Tooltip title={v}><Text type="secondary" style={{ fontSize: 12 }}>{v}</Text></Tooltip> : null,
    },
  ];

  return (
    <div>
      {/* ── Summary Cards ────────────────────────────────────────────────── */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ background: '#f0f5ff', borderColor: '#adc6ff' }}>
            <Statistic
              title="Bill Amount"
              value={Number(billAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              prefix="₹"
              valueStyle={{ fontSize: 15, color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}>
            <Statistic
              title="Total Paid"
              value={Number(totalPaid).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              prefix="₹"
              valueStyle={{ fontSize: 15, color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card
            size="small"
            style={{
              background: outstanding > 0 ? '#fffbe6' : '#f6ffed',
              borderColor: outstanding > 0 ? '#ffe58f' : '#b7eb8f',
            }}
          >
            <Statistic
              title="Outstanding"
              value={Number(outstanding).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              prefix="₹"
              valueStyle={{ fontSize: 15, color: outstanding > 0 ? '#fa8c16' : '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <div style={{ marginBottom: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Payment Status</Text>
            </div>
            {getPaymentStatusTag(payStatus)}
            <Progress
              percent={paidPercent}
              size="small"
              showInfo={false}
              strokeColor={paidPercent === 100 ? '#52c41a' : '#1890ff'}
              style={{ marginTop: 6, marginBottom: 0 }}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>{paidPercent}% paid</Text>
          </Card>
        </Col>
      </Row>

      {/* ── Header row ───────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <Space>
          <Text strong>Payment History</Text>
          {payments.length > 0 && (
            <Tag color="blue">{payments.length} record{payments.length > 1 ? 's' : ''}</Tag>
          )}
        </Space>
        <Space>
          <Button size="small" icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>
            Refresh
          </Button>
          {canCreate && bill.bill_status !== 'rejected' && outstanding > 0.01 && (
            <Button
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setModalOpen(true)}
            >
              Record Payment
            </Button>
          )}
        </Space>
      </div>

      {/* ── Payments table ───────────────────────────────────────────────── */}
      {loading && !cached ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : payments.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span>
              No payments recorded yet.{' '}
              {canCreate && outstanding > 0.01 && bill.bill_status !== 'rejected' && (
                <Button type="link" size="small" onClick={() => setModalOpen(true)}>
                  Record the first payment
                </Button>
              )}
            </span>
          }
        />
      ) : (
        <Table
          columns={columns}
          dataSource={payments}
          rowKey="id"
          size="small"
          pagination={false}
          scroll={{ x: 760 }}
          loading={loading}
        />
      )}

      {/* ── Record Payment Modal ──────────────────────────────────────────── */}
      <RecordPaymentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        bill={bill}
        paymentSummary={summary}
      />
    </div>
  );
};

export default PaymentPanel;
